import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import Facebook from '@mui/icons-material/Facebook';
import Instagram from '@mui/icons-material/Instagram';
import WhatsApp from '@mui/icons-material/WhatsApp';
import Phone from '@mui/icons-material/Phone';
import LocationOn from '@mui/icons-material/LocationOn';
import './menu.css';

const Menu = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  // Primary: /menu?id=xxx — also supports /menu/xxx
  const restaurantId = searchParams.get('id') || params.id;

  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState('en');
  const [expandedCategory, setExpandedCategory] = useState(0);
  const [expandedSubcategories, setExpandedSubcategories] = useState({});

  useEffect(() => {
    if (restaurantId) {
      axios
        .get(`/getPublicResturent/${restaurantId}`)
        .then((res) => {
          setRestaurant(res.data);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.response?.data?.message || 'Failed to load menu');
          setLoading(false);
        });
    } else {
      setError('No restaurant ID provided');
      setLoading(false);
    }
  }, [restaurantId]);

  // Initial language: Arabic only → ar, else en (for both we default to en; user can switch)
  useEffect(() => {
    if (restaurant?.menuLanguage === 'ar') {
      setLanguage('ar');
    } else if (restaurant && !restaurant.menuLanguage && !restaurant.isBilingual) {
      setLanguage('en');
    }
  }, [restaurant]);

  const toggleCategory = (index) => {
    setExpandedCategory(expandedCategory === index ? null : index);
    setExpandedSubcategories({});
  };

  const toggleSubcategory = (subIndex) => {
    setExpandedSubcategories((prev) => ({
      ...prev,
      [subIndex]: !prev[subIndex]
    }));
  };

  // Use Arabic content when language is ar and restaurant supports Arabic (ar only, or both)
  const useArabicContent = language === 'ar' && (restaurant?.menuLanguage === 'ar' || restaurant?.menuLanguage === 'both' || restaurant?.isBilingual);
  const showLanguageToggle = restaurant?.menuLanguage === 'both' || (restaurant?.isBilingual && restaurant?.menuLanguage !== 'ar');

  const getName = (item) => {
    if (useArabicContent && item.nameAr) return item.nameAr;
    return item.name;
  };

  const getTitle = (item) => {
    if (useArabicContent && item.titleAr) return item.titleAr;
    return item.title;
  };

  const getDesc = (item) => {
    if (useArabicContent && item.descAr) return item.descAr;
    return item.desc;
  };

  const getCategoryName = (category) => {
    if (useArabicContent && category.categoryNameAr) return category.categoryNameAr;
    return category.categoryName;
  };

  const getRestaurantName = () => {
    if (useArabicContent && restaurant?.resturentNameAr) return restaurant.resturentNameAr;
    return restaurant?.resturentName;
  };

  const getLocationLabel = () => {
    if (useArabicContent && restaurant?.locationLabelAr) return restaurant.locationLabelAr;
    return restaurant?.locationLabel || restaurant?.location || '';
  };

  if (loading) {
    return (
      <div className="menu-container">
        <div className="menu-loading">
          <div className="spinner"></div>
          <p>Loading menu...</p>
        </div>
      </div>
    );
  }

  if (error || !restaurant) {
    return (
      <div className="menu-container">
        <div className="menu-error">
          <span className="material-symbols-outlined">error</span>
          <h2>Menu Not Found</h2>
          <p>{error || 'Restaurant not found'}</p>
        </div>
      </div>
    );
  }

  // Check if restaurant menu is expired
  const today = new Date().toISOString().split('T')[0];
  const expireDateStr = restaurant.expireDate
    ? new Date(restaurant.expireDate).toISOString().split('T')[0]
    : null;
  const isExpired = expireDateStr && expireDateStr <= today;

  if (isExpired) {
    return (
      <div
        className="menu-container"
        style={{
          '--menu-primary': restaurant.primaryColor || '#ec4913',
          '--menu-bg': restaurant.backgroundColor || '#1a1410',
          background: `linear-gradient(135deg, ${restaurant.backgroundColor || '#1a1410'} 0%, ${restaurant.backgroundColor || '#1a1410'}dd 100%)`
        }}
      >
        <div className="menu-expired-wrapper">
          <div className="menu-expired-message">
            <img
              src={restaurant.resturentImage || 'https://ccnfctag.com/uploads/1744662353893.jpeg'}
              className="menu-expired-message__image"
              alt={restaurant.resturentName || 'Restaurant'}
            />
            <p className="menu-expired-text">This menu is expired. Please contact support.</p>
            <a
              href="https://wa.me/962785444032"
              target="_blank"
              rel="noopener noreferrer"
              className="menu-expired-whatsapp-btn"
            >
              <WhatsApp sx={{ fontSize: 24 }} />
              <span>Contact support on WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  const primaryColor = restaurant.primaryColor || '#ec4913';
  const backgroundColor = restaurant.backgroundColor || '#1a1410';

  return (
    <div
      className="menu-container"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      style={{
        '--menu-primary': primaryColor,
        '--menu-bg': backgroundColor,
        '--menu-bg-gradient': `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}dd 100%)`,
        background: `linear-gradient(135deg, ${backgroundColor} 0%, ${backgroundColor}dd 100%)`
      }}
    >
      <main className="menu-main">
        {/* Restaurant Header */}
        <section className="restaurant-header">
          <div className="header-content">
            {restaurant.resturentImage && (
              <div className="restaurant-logo">
                <img src={restaurant.resturentImage} alt={getRestaurantName()} />
              </div>
            )}
            {!restaurant.resturentImage && (
              <div className="restaurant-icon">
                <span className="material-symbols-outlined">restaurant</span>
              </div>
            )}
            <div>
              <h1 className="restaurant-name">{getRestaurantName()}</h1>
            </div>

            {/* Location (top) – label only, no link */}
            {(restaurant.location || restaurant.locationLabel || restaurant.locationLabelAr) && getLocationLabel() && (
              <div className="header-location">
                <LocationOn sx={{ fontSize: 20 }} className="header-location-icon" />
                <span className="header-location-label">{getLocationLabel()}</span>
              </div>
            )}

            {/* Social & Phone Links */}
            {(restaurant.phoneNumber || restaurant.facebookLink || restaurant.instagramLink || restaurant.whatsappLink) && (
              <div className="header-social">
                {restaurant.phoneNumber && (
                  <a
                    href={`tel:${restaurant.phoneNumber.replace(/\D/g, '')}`}
                    className="header-social-link"
                    title="Call"
                    aria-label="Call"
                  >
                    <Phone sx={{ fontSize: 22 }} />
                  </a>
                )}
                {restaurant.facebookLink && (
                  <a
                    href={restaurant.facebookLink.startsWith('http') ? restaurant.facebookLink : `https://${restaurant.facebookLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="header-social-link"
                    title="Facebook"
                    aria-label="Facebook"
                  >
                    <Facebook sx={{ fontSize: 22 }} />
                  </a>
                )}
                {restaurant.instagramLink && (
                  <a
                    href={restaurant.instagramLink.startsWith('http') ? restaurant.instagramLink : `https://${restaurant.instagramLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="header-social-link"
                    title="Instagram"
                    aria-label="Instagram"
                  >
                    <Instagram sx={{ fontSize: 22 }} />
                  </a>
                )}
                {restaurant.whatsappLink && (
                  <a
                    href={restaurant.whatsappLink.startsWith('http') ? restaurant.whatsappLink : `https://wa.me/${restaurant.whatsappLink.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="header-social-link"
                    title="WhatsApp"
                    aria-label="WhatsApp"
                  >
                    <WhatsApp sx={{ fontSize: 22 }} />
                  </a>
                )}
              </div>
            )}

            {/* Language Toggle */}
            {showLanguageToggle && (
              <div className="language-toggle">
                <button
                  className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                  onClick={() => setLanguage('en')}
                >
                  English
                </button>
                <button
                  className={`lang-btn ${language === 'ar' ? 'active' : ''}`}
                  onClick={() => setLanguage('ar')}
                >
                  العربية
                </button>
              </div>
            )}

            <div className="divider"></div>
          </div>
        </section>

        {/* Menu Categories */}
        <div className="categories-container">
          {restaurant.categories?.map((category, catIndex) => (
            <div key={catIndex} className="category-card">
              <button
                className="category-header"
                onClick={() => toggleCategory(catIndex)}
              >
                <div className="category-header-top">
                  <span className="category-title">{getCategoryName(category)}</span>
                  <span className={`material-symbols-outlined arrow ${expandedCategory === catIndex ? 'expanded' : ''}`}>
                    expand_more
                  </span>
                </div>
                {category.categoryImage && (
                  <img src={category.categoryImage} alt="" className="category-image" />
                )}
              </button>

              {expandedCategory === catIndex && (
                <div className="category-content">
                  {/* Items directly in category (no subcategory) */}
                  {category.items?.length > 0 && (
                    <div className="subcategory-section">
                      <div className="items-list">
                        {category.items.map((item, itemIndex) => (
                          <div key={`dir-${itemIndex}`} className="menu-item">
                            <div className="item-header">
                              <h4 className="item-name">{getName(item) || getTitle(item)}</h4>
                              <div className="item-dots"></div>
                              <span className="item-price">{restaurant.currency ? `${restaurant.currency} ` : '$ '}{item.price}</span>
                            </div>
                            {getDesc(item) && (
                              <p className="item-description">{getDesc(item)}</p>
                            )}
                            {item.image && (
                              <div className="item-image-container">
                                <img src={item.image} alt={getName(item)} className="item-image" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {category.subCategories?.map((subcategory, subIndex) => (
                    <div key={subIndex} className="subcategory-section">
                      <button
                        className="subcategory-header"
                        onClick={() => toggleSubcategory(subIndex)}
                      >
                        <span className="subcategory-name">{getName(subcategory)}</span>
                        <span className={`material-symbols-outlined ${expandedSubcategories[subIndex] ? 'expanded' : ''}`}>
                          keyboard_arrow_down
                        </span>
                      </button>

                      {expandedSubcategories[subIndex] && (
                        <div className="items-list">
                          {subcategory.items?.map((item, itemIndex) => (
                            <div key={itemIndex} className="menu-item">
                              <div className="item-header">
                                <h4 className="item-name">{getName(item) || getTitle(item)}</h4>
                                <div className="item-dots"></div>
                                <span className="item-price">{restaurant.currency ? `${restaurant.currency} ` : '$ '}{item.price}</span>
                              </div>
                              {getDesc(item) && (
                                <p className="item-description">{getDesc(item)}</p>
                              )}
                              {item.image && (
                                <div className="item-image-container">
                                  <img src={item.image} alt={getName(item)} className="item-image" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Menu;
